import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Upload, Download, FileText } from 'lucide-react';
import Papa from 'papaparse';

interface CSVImporterProps {
  settings: any;
}

interface StudentRecord {
  name: string;
  student_id: string;
  grade: string;
  section: string;
  phone?: string;
  parent_phone?: string;
  address?: string;
}

export const CSVImporter = ({ settings }: CSVImporterProps) => {
  const { toast } = useToast();
  const [isImporting, setIsImporting] = useState(false);
  const [file, setFile] = useState<File | null>(null);

  const downloadTemplate = () => {
    const csvTemplate = 'اسم الطالب,رقم الطالب,الصف,الشعبة,رقم الهاتف,رقم هاتف ولي الأمر,العنوان\n' +
                        'أحمد محمد علي,001,الصف الأول,أ,0501234567,0509876543,الرياض\n' +
                        'فاطمة عبدالله,002,الصف الأول,أ,0501234568,0509876544,الرياض\n' +
                        'محمد أحمد السعد,003,الصف الثاني,ب,0501234569,0509876545,الرياض';

    const blob = new Blob([csvTemplate], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'نموذج_استيراد_الطلاب.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: "تم التحميل",
      description: "تم تحميل نموذج CSV للطلاب",
      variant: "default"
    });
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.type !== 'text/csv' && !selectedFile.name.endsWith('.csv')) {
        toast({
          title: "نوع ملف غير صحيح",
          description: "يرجى تحديد ملف CSV فقط",
          variant: "destructive"
        });
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleImport = async () => {
    if (!file) {
      toast({
        title: "لم يتم تحديد ملف",
        description: "يرجى تحديد ملف CSV أولاً",
        variant: "destructive"
      });
      return;
    }

    setIsImporting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('غير مسموح');

      const schoolId = user.id;

      // Parse CSV file
      Papa.parse(file, {
        header: true,
        encoding: 'UTF-8',
        complete: async (results) => {
          try {
            const data = results.data as any[];
            const validRecords: StudentRecord[] = [];
            const errors: string[] = [];

            // Map CSV headers to our database fields
            const headerMap: { [key: string]: string } = {
              'اسم الطالب': 'name',
              'رقم الطالب': 'student_id',
              'الصف': 'grade',
              'الشعبة': 'section',
              'رقم الهاتف': 'phone',
              'رقم هاتف ولي الأمر': 'parent_phone',
              'العنوان': 'address'
            };

            data.forEach((row, index) => {
              const record: any = {};
              let hasRequiredFields = true;

              // Map headers to database fields
              Object.keys(row).forEach(csvHeader => {
                const dbField = headerMap[csvHeader.trim()];
                if (dbField) {
                  record[dbField] = row[csvHeader]?.toString().trim() || '';
                }
              });

              // Validate required fields
              if (!record.name || !record.student_id || !record.grade || !record.section) {
                errors.push(`الصف ${index + 2}: بيانات ناقصة (الاسم، رقم الطالب، الصف، والشعبة مطلوبة)`);
                hasRequiredFields = false;
              }

              if (hasRequiredFields) {
                validRecords.push({
                  name: record.name,
                  student_id: record.student_id,
                  grade: record.grade,
                  section: record.section,
                  phone: record.phone || null,
                  parent_phone: record.parent_phone || null,
                  address: record.address || null
                });
              }
            });

            if (errors.length > 0) {
              toast({
                title: "أخطاء في البيانات",
                description: `تم العثور على ${errors.length} خطأ. يرجى مراجعة الملف وإعادة المحاولة.`,
                variant: "destructive"
              });
              console.error('CSV parsing errors:', errors);
              return;
            }

            if (validRecords.length === 0) {
              toast({
                title: "لا توجد بيانات صالحة",
                description: "لم يتم العثور على أي بيانات صالحة في الملف",
                variant: "destructive"
              });
              return;
            }

            // Prepare data for insertion
            const studentsToInsert = validRecords.map(record => ({
              school_id: schoolId,
              name: record.name,
              student_id: record.student_id,
              grade: record.grade,
              section: record.section,
              phone: record.phone,
              parent_phone: record.parent_phone,
              address: record.address,
              status: 'active'
            }));

            // Insert into database
            const { data: insertedData, error: insertError } = await supabase
              .from('students')
              .insert(studentsToInsert)
              .select();

            if (insertError) {
              if (insertError.code === '23505') { // Unique constraint violation
                toast({
                  title: "خطأ في البيانات",
                  description: "يوجد طلاب بأرقام مكررة. يرجى التأكد من عدم تكرار أرقام الطلاب",
                  variant: "destructive"
                });
              } else {
                throw insertError;
              }
              return;
            }

            toast({
              title: "تم الاستيراد بنجاح",
              description: `تم استيراد ${validRecords.length} طالب بنجاح`,
              variant: "default"
            });

            setFile(null);
            // Reset file input
            const fileInput = document.getElementById('csv-file') as HTMLInputElement;
            if (fileInput) fileInput.value = '';

          } catch (error) {
            console.error('Error importing data:', error);
            toast({
              title: "خطأ في الاستيراد",
              description: "حدث خطأ أثناء استيراد البيانات",
              variant: "destructive"
            });
          } finally {
            setIsImporting(false);
          }
        },
        error: (error) => {
          console.error('CSV parsing error:', error);
          toast({
            title: "خطأ في قراءة الملف",
            description: "حدث خطأ أثناء قراءة ملف CSV",
            variant: "destructive"
          });
          setIsImporting(false);
        }
      });
    } catch (error) {
      console.error('Error importing CSV:', error);
      toast({
        title: "خطأ في الاستيراد",
        description: "حدث خطأ أثناء استيراد البيانات",
        variant: "destructive"
      });
      setIsImporting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-blue-700">
          <Upload className="w-5 h-5" />
          استيراد بيانات الطلاب
        </CardTitle>
        <CardDescription>
          قم بتحميل ملف CSV لاستيراد بيانات الطلاب إلى النظام
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800 mb-2">
              تعليمات استيراد البيانات:
            </p>
            <ul className="text-sm text-blue-700 space-y-1 mr-4">
              <li>• يجب أن يكون الملف بصيغة CSV</li>
              <li>• الأعمدة المطلوبة: اسم الطالب، رقم الطالب، الصف، الشعبة</li>
              <li>• الأعمدة الاختيارية: رقم الهاتف، رقم هاتف ولي الأمر، العنوان</li>
              <li>• يجب عدم تكرار أرقام الطلاب</li>
            </ul>
          </div>

          <div className="flex gap-2">
            <Button 
              onClick={downloadTemplate}
              variant="outline"
              className="flex-1"
            >
              <Download className="w-4 h-4 ml-2" />
              تحميل نموذج CSV
            </Button>
          </div>

          <div className="space-y-2">
            <Label htmlFor="csv-file">تحديد ملف CSV:</Label>
            <Input
              id="csv-file"
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="cursor-pointer"
              dir="ltr"
            />
          </div>

          {file && (
            <div className="bg-green-50 p-3 rounded-lg border border-green-200">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-green-600" />
                <span className="text-sm text-green-800">
                  تم تحديد الملف: {file.name}
                </span>
              </div>
            </div>
          )}

          <Button 
            onClick={handleImport}
            disabled={!file || isImporting}
            className="w-full bg-blue-600 hover:bg-blue-700"
          >
            {isImporting ? 'جاري الاستيراد...' : 'استيراد البيانات'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};